<template>
  <div class="page new-game">
    <div class="step" v-if="!gameModel.mode">
      <div class="rules">
        <h1>Watsdat</h1>
        <p>Try to guess what you are looking at 👀</p>
      </div>
      <h2>Pick a category</h2>
      <div class="category">
        <h3>Most Popular</h3>
        <div class="container">
          <a class="game-mode" v-for="mode in gameModes.popular" :key="mode.mode" @click="selectMode(mode)" :style="{ backgroundColor: mode.color }">
            <div class="game-mode-content">
              <div class="game-mode-title">{{ mode.title }}</div>
              <div class="game-mode-description">{{ mode.description }}</div>
            </div>
          </a>
        </div>
      </div>
      <div class="category">
        <h3>By Release Date</h3>
        <div class="container">
          <a class="game-mode" v-for="mode in gameModes.release" :key="mode.mode" @click="selectMode(mode)" :style="{ backgroundColor: mode.color }">
            <div class="game-mode-content">
              <div class="game-mode-title">{{ mode.title }}</div>
              <div class="game-mode-description">{{ mode.description }}</div>
            </div>
          </a>
        </div>
      </div>
      <div class="category">
        <h3>By Genre</h3>
        <div class="container">
          <a class="game-mode" v-for="mode in gameModes.genre" :key="mode.mode" @click="selectMode(mode)" :style="{ backgroundColor: mode.color }">
            <div class="game-mode-content">
              <div class="game-mode-title">{{ mode.title }}</div>
              <div class="game-mode-description">{{ mode.description }}</div>
            </div>
          </a>
        </div>
      </div>
      <div class="category">
        <h3>By Theme</h3>
        <div class="container">
          <a class="game-mode" v-for="mode in gameModes.theme" :key="mode.mode" @click="selectMode(mode)" :style="{ backgroundColor: mode.color }">
            <div class="game-mode-content">
              <div class="game-mode-title">{{ mode.title }}</div>
              <div class="game-mode-description">{{ mode.description }}</div>
            </div>
          </a>
        </div>
      </div>
      <div class="category">
        <h3>By Brand</h3>
        <div class="container">
          <a class="game-mode" v-for="mode in gameModes.brand" :key="mode.mode" @click="selectMode(mode)" :style="{ backgroundColor: mode.color }">
            <div class="game-mode-content">
              <div class="game-mode-title">{{ mode.title }}</div>
              <div class="game-mode-description">{{ mode.description }}</div>
            </div>
          </a>
        </div>
      </div>
    </div>
    <div class="step game-panel" v-else>
      <div class="game-browser">
        <h2>Browse Games: {{ gameModel.mode.mode }}</h2>
        <div class="games">
          <router-link :to="`/games/${game.id}`" class="game" v-for="game in games" :key="game.id">
            <div class="game-name">{{ game.name }}</div>
            <div class="game-details">
              <div class="game-data" title="Players">
                <span class="material-symbols-outlined">person</span>
                {{ Object.keys(game.players).length }} / 8
              </div>
              <div class="game-data" title="Rounds">
                <span class="material-symbols-outlined">sync</span>
                {{ game.status === GameStatus.Lobby ? 0 : game.current_round + 1 }} / {{ game.settings.max_rounds }}
              </div>
            </div>
          </router-link>
        </div>
      </div>
      <div class="game-settings" v-if="!store.state.user?.name">
        <h2>New Game</h2>
        <form @submit.prevent="updateUser">
          <div class="field">
            <label for="name">Username</label>
            <input v-model="userModel.name" id="name" class="input" type="text" placeholder="Enter a username..." minlength="1" maxlength="20" :disabled="loading" required>
          </div>
          <div class="actions">
            <button class="btn" :disabled="loading || !userModel.name">Create Game</button>
          </div>
        </form>
      </div>
      <div class="game-settings" v-else-if="!gameModel.type">
        <h2>New Game</h2>
        <a class="game-mode" @click="gameModel.type = GameType.Multi">
          <div class="game-mode-content">
            <div class="game-mode-title">Multiplayer</div>
            <div class="game-mode-description">Play with your friends or complete strangers!</div>
          </div>
        </a>
        <a class="game-mode" style="background-color: var(--red-600);" @click="gameModel.type = GameType.Challenge">
          <div class="game-mode-content">
            <div class="game-mode-title">Challenge</div>
            <div class="game-mode-description">Guess as many answers as possible within 3 minutes!</div>
          </div>
        </a>
      </div>
      <div class="game-settings" v-else>
        <h2>New Game</h2>
        <form @submit.prevent="createGame" v-if="gameModel.type === GameType.Multi">
          <div class="field">
            <label for="name">Name</label>
            <input v-model="gameModel.name" id="name" class="input" type="text" placeholder="Name of the game" minlength="1" maxlength="50" :disabled="loading" required>
          </div>
          <div class="field">
            <label class="inline" for="mode">
              Mode
              <button class="btn small" type="button" @click="selectMode(null)">Change</button>
            </label>
            <div class="game-mode" :style="{ background: gameModel.mode.color }">
              <div class="game-mode-content">
                <div class="game-mode-title">{{ gameModel.mode.title }}</div>
                <div class="game-mode-description">{{ gameModel.mode.description }}</div>
              </div>
            </div>
          </div>
          <div class="field">
            <label for="rounds">Number of rounds</label>
            <div class="input-range">
              <input v-model="gameModel.rounds" id="rounds" type="range" min="10" max="100" step="10" :disabled="loading" required>
              <b>{{ gameModel.rounds }} rounds</b>
            </div>
          </div>
          <div class="field">
            <label for="duration">Round duration</label>
            <div class="input-range">
              <input v-model="gameModel.duration" id="duration" type="range" min="15" max="60" step="15" :disabled="loading" required>
              <b>{{ gameModel.duration }} seconds</b>
            </div>
          </div>
          <div class="field">
            <label for="attempts">Number of attempts</label>
            <div class="input-range">
              <input v-model="gameModel.attempts" id="attempts" type="range" min="0" max="10" step="1" :disabled="loading" required>
              <b>{{ gameModel.attempts || 'Unlimited' }} attempts</b>
            </div>
          </div>
          <div class="field inline">
            <ToggleButton id="public" v-model="gameModel.public" :disabled="loading" />
            <label for="public">Public game</label>
          </div>
          <div class="actions">
            <button type="button" class="btn outlined" :disabled="loading" @click="gameModel.type = null">Back</button>
            <button class="btn" :disabled="loading || !gameModel.name">Create Game</button>
          </div>
        </form>
        <form @submit.prevent="createGame" v-else>
          <div class="actions">
            <button type="button" class="btn outlined" :disabled="loading" @click="gameModel.type = null">Back</button>
            <button class="btn" :disabled="loading">Create Game</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { client } from '@/api';
import { Game, GameMode, GameStatus, GameType } from '@/api/types';
import { StoreParams } from '@/store/types';
import { onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ToggleButton from '../components/ToggleButton.vue';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/firebase';

interface GameModeOption {
  mode: GameMode;
  title: string;
  description: string;
  color: string;
}

const store = useStore<StoreParams>();
const router = useRouter();
const userModel = ref({ name: '' });
const gameModel = ref<{ mode: any, name: string, type: GameType | null, rounds: number, duration: number, attempts: number, public: boolean }>({
  mode: null,
  name: `${store.state.user?.name}'s game`,
  type: null,
  rounds: 20,
  duration: 30,
  attempts: 0,
  public: true,
});
const gameModes = ref({
  popular: [
    { mode: GameMode.Standard, title: 'Most popular of all time', description: 'The top games from all eras and platforms.', color: 'var(--blue-600)' },
  ],
  release: [
    { mode: GameMode['80s'], title: '80s', description: 'Popular games from the 80s', color: 'var(--orange-600)' },
    { mode: GameMode['90s'], title: '90s', description: 'Popular games from the 90s', color: 'var(--indigo-600)' },
    { mode: GameMode['2000s'], title: '2000s', description: 'Popular games from the 2000s', color: 'var(--purple-600)' },
    { mode: GameMode['2010s'], title: '2010s', description: 'Popular games from the 2010s', color: 'var(--red-600)' },
  ],
  genre: [
    { mode: GameMode.Action, title: 'Action', description: 'The most popular action games of all time', color: 'var(--cyan-500)' },
    { mode: GameMode.Adventure, title: 'Adventure', description: 'The most popular adventure games of all time', color: 'var(--green-500)' },
    { mode: GameMode.Indie, title: 'Indie', description: 'The most popular games made by indie developers', color: 'var(--red-400)' },
    { mode: GameMode.Music, title: 'Music', description: 'The most popular music games of all time', color: 'var(--pink-500)' },
    { mode: GameMode.Platformer, title: 'Platformer', description: 'The most popular platformer games of all time', color: 'var(--yellow-500)' },
    { mode: GameMode.RPG, title: 'RPG', description: 'The most popular RPG games of all time', color: 'var(--indigo-500)' },
    { mode: GameMode.Racing, title: 'Racing', description: 'The most popular racing games of all time', color: 'var(--blue-500)' },
    { mode: GameMode.Shooter, title: 'Shooter', description: 'The most popular shooters of all time', color: 'var(--teal-500)' },
    { mode: GameMode.Sport, title: 'Sport', description: 'The most popular sport games of all time', color: 'var(--orange-500)' },
    { mode: GameMode.Strategy, title: 'Strategy', description: 'The most popular strategy games of all time', color: 'var(--purple-500)' },
  ],
  theme: [
    { mode: GameMode.Drama, title: 'Drama', description: 'Popular games about people and feelings', color: 'var(--indigo-400)' },
    { mode: GameMode.Fantasy, title: 'Fantasy', description: 'Popular games about dragons and stuff', color: 'var(--cyan-400)' },
    { mode: GameMode.Horror, title: 'Horror', description: 'The scariest games of all time', color: 'var(--bluegray-900)' },
    { mode: GameMode.Romance, title: 'Romance', description: 'The top games about LUUUUUV', color: 'var(--pink-800)' },
  ],
  brand: [
    { mode: GameMode.PlayStation, title: 'PlayStation', description: 'Popular games published on PlayStation consoles', color: 'var(--indigo-400)' },
    { mode: GameMode.Nintendo, title: 'Nintendo', description: 'Popular games published on Nintendo consoles', color: 'var(--purple-400)' },
    { mode: GameMode.XBOX, title: 'XBOX', description: 'Popular games published on XBOX consoles', color: 'var(--green-400)' },
    { mode: GameMode.Sega, title: 'Sega', description: 'Popular games published on Sega consoles', color: 'var(--red-400)' },
  ],
});
const loading = ref(false);
const gamesRef = ref();
const games = ref<Game[]>([]);

const selectMode = (mode: GameModeOption | null) => {
  gamesRef.value?.();
  gameModel.value = {
    ...gameModel.value,
    mode,
  };
  games.value = [];
  window.scrollTo({ top: 0 });
  if (!mode) {
    return;
  }
  gamesRef.value = onSnapshot(query(collection(db, 'games'), where('settings.public', '==', true), where('mode', '==', mode.mode), where('status', '!=', 'ended'), orderBy('created_at', 'desc'), limit(50)), (snap) => {
    games.value = snap.docs.map((game) => game.data() as Game);
    console.log(games.value);
  });
};

const updateUser = async () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  try {
    const user = await client.post('/me', { name: userModel.value.name });
    gameModel.value = {
      name: `${user.name}'s game`,
      mode: null,
      type: null,
      rounds: 20,
      duration: 30,
      attempts: 0,
      public: true,
    };
    store.commit('setUser', user);
  } catch (error) {
    console.warn('UPDATE_USER_ERROR', error);
  }
  loading.value = false;
};

const createGame = async () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  try {
    const { name, rounds, mode, duration, attempts, public: isPublic, type } = gameModel.value;
    const game = await client.post('/games', {
      name,
      rounds,
      type,
      mode: mode.mode,
      duration,
      attempts,
      public: isPublic,
    });
    router.push(`/games/${game.id}`);
  } catch (error) {
    console.warn('CREATE_GAME_ERROR', error);
  }
  loading.value = false;
};

onBeforeUnmount(() => {
  gamesRef.value?.();
});
</script>

<style scoped>
.page {
  padding: 24px;
}

.rules,
.game-settings,
.game-browser {
  width: 480px;
  max-width: 100%;
  background: rgba(0, 0, 0, 0.48);
  border-radius: 16px;
  padding: 24px;
}

.game-settings {
  position: sticky;
  top: 24px;
}

.rules {
  text-align: center;
  margin: auto;
  font-size: 20px;
  margin-bottom: 24px;
}

.rules h1 {
  margin-bottom: 16px;
  color: #FC0;
}

.game-settings h2,
.game-browser h2 {
  text-align: center;
  margin-bottom: 24px;
  text-transform: capitalize;
}

.game-settings .field {
  margin-bottom: 24px;
}

.game-settings .field.inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-settings .field.inline label {
  margin: 0;
}

.game-settings .actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.container {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.category {
  margin-top: 24px;
}

h3 {
  margin-bottom: 12px;
}

.game-mode {
  position: relative;
  display: block;
  border-radius: 12px;
  background: var(--blue-600);
  color: #fff;
  font-size: 18px;
  width: 380px;
  max-width: 100%;
  overflow: hidden;
}

.game-mode-content {
  padding: 16px 24px;
  min-height: 140px;
  background: linear-gradient(160deg, rgba(0, 0, 0, 0.32) 0%, rgba(0, 0, 0, 0.04) 100%);
}

.game-mode-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 12px;
}

.game-panel {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  width: 1280px;
  max-width: 100%;
  margin: auto;
}

.game-browser {
  flex-grow: 1;
  margin: 0;
  min-height: calc(100vh - 132px);
}

.game {
  display: block;
  background: #393652;
  border-radius: 8px;
  padding: 8px 16px;
  margin-bottom: 12px;
}

.game .game-name {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
}

.game .game-details {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.game .game-details .game-data {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.48);
  padding: 6px 12px;
  font-size: 14px;
}

.game .game-details .game-data span {
  font-size: 16px;
}

.game-settings .game-mode {
  width: 100%;
  margin-top: 8px;
}

@media screen and (max-width: 1170px) {
  .game-panel {
    flex-direction: column-reverse;
    align-items: center;
  }
  .game-settings {
    position: relative;
    top: 0;
  }
  .game-browser {
    width: 100%;
  }
}
</style>
